import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { type RootState, type BundleTypeKeys, BundleType } from '@/store';
import { PAGE_SCALE_MAX_VALUE, PAGE_SCALE_MIN_VALUE, PAGE_SCALE_STEP } from '@/constants';

interface BundleRetrieveState {
  bundleRetrievePageIds: string[];
  bundleRetrieveBasePageId: string | null;
  bundleRetrieveActivePageIndex: number;
  bundleRetrievePageScale: number;
  bundleRetrievePageSelectedCheckboxes: string[];
}

const initialState: BundleRetrieveState = {
  bundleRetrievePageIds: [],
  bundleRetrieveBasePageId: null,
  bundleRetrieveActivePageIndex: 0,
  bundleRetrievePageScale: 1,
  bundleRetrievePageSelectedCheckboxes: []
};

const bundleRetrieveSlice = createSlice({
  name: 'bundleRetrieve',
  initialState: {
    [BundleType.LIGHT]: initialState,
    [BundleType.FULL]: initialState
  } as Record<BundleTypeKeys, BundleRetrieveState>,
  reducers: {
    setBundleRetrievePageIds: (state, action: PayloadAction<{ key: BundleTypeKeys; data: string[] }>) => {
      const { key, data } = action.payload;
      state[key].bundleRetrievePageIds = data;
    },
    setBundleRetrieveBasePageId: (state, action: PayloadAction<{ key: BundleTypeKeys; data: string }>) => {
      const { key, data } = action.payload;
      state[key].bundleRetrieveBasePageId = data;
    },
    setBundleRetrieveActivePageIndex: (state, action: PayloadAction<{ key: BundleTypeKeys; data: number }>) => {
      const { key, data } = action.payload;
      state[key].bundleRetrieveActivePageIndex = data;
    },
    setBundleRetrievePageScaleZoomIn: (state, action: PayloadAction<{ key: BundleTypeKeys }>) => {
      const { key } = action.payload;
      const newValue = Math.min(state[key].bundleRetrievePageScale + PAGE_SCALE_STEP, PAGE_SCALE_MAX_VALUE);
      state[key].bundleRetrievePageScale = newValue;
    },
    setBundleRetrievePageScaleZoomOut: (state, action: PayloadAction<{ key: BundleTypeKeys }>) => {
      const { key } = action.payload;
      const newValue = Math.max(state[key].bundleRetrievePageScale - PAGE_SCALE_STEP, PAGE_SCALE_MIN_VALUE);
      state[key].bundleRetrievePageScale = newValue;
    },
    setBundleRetrievePageSelectedCheckboxes: (
      state,
      action: PayloadAction<{ key: BundleTypeKeys; data: string[] }>
    ) => {
      const { key, data } = action.payload;
      state[key].bundleRetrievePageSelectedCheckboxes = data;
    },
    resetBundleRetrievePage: (state, action: PayloadAction<{ key: BundleTypeKeys }>) => {
      const { key } = action.payload;
      state[key] = {
        ...initialState,
        bundleRetrieveBasePageId: state[key].bundleRetrieveBasePageId
      };
    }
  }
});

export const {
  setBundleRetrievePageIds,
  setBundleRetrieveBasePageId,
  setBundleRetrieveActivePageIndex,
  setBundleRetrievePageScaleZoomIn,
  setBundleRetrievePageScaleZoomOut,
  setBundleRetrievePageSelectedCheckboxes,
  resetBundleRetrievePage
} = bundleRetrieveSlice.actions;

// RTK SELECTORS WITHOUT CALLBACK

export const selectBundleRetrievePageIds = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleRetrieve[activeBundleType].bundleRetrievePageIds
    : initialState.bundleRetrievePageIds;
};

export const selectBundleRetrieveBasePageId = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleRetrieve[activeBundleType].bundleRetrieveBasePageId
    : initialState.bundleRetrieveBasePageId;
};

export const selectBundleRetrieveActivePageIndex = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleRetrieve[activeBundleType].bundleRetrieveActivePageIndex
    : initialState.bundleRetrieveActivePageIndex;
};

export const selectBundleRetrievePageScale = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleRetrieve[activeBundleType].bundleRetrievePageScale
    : initialState.bundleRetrievePageScale;
};

export const selectBundleRetrievePageSelectedCheckboxes = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleRetrieve[activeBundleType].bundleRetrievePageSelectedCheckboxes
    : initialState.bundleRetrievePageSelectedCheckboxes;
};

export const bundleRetrieveReducer = bundleRetrieveSlice.reducer;
